
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
    import moment from 'moment';
    import Rezept from '@/types/Rezept';
    import { confirmSwal } from '@/helper/swals';
    import EingetragenAmVom from '@/components/EingetragenAmVom.vue';
    import { SweetAlertResult } from 'sweetalert2';
    import PatRezept from '@/types/PatRezept';
    import Rezepteintrag from '@/types/Rezepteintrag';
    import { ValidationRules } from '@/store/types';
    import { VForm } from '@/types/Components';
    import Karteieintrag from '@/types/Karteieintrag';
    import Patient from '@/types/Patient';
    import { LoadPersonenPayload } from '@/store/personen';
    import { LoadRezeptePayload } from '@/store/rezepte';

    @Component({
        components: {
            EingetragenAmVom,
        },
        computed: {
            ...mapState('globals', ['rules', 'formatDate', 'parseDate']),
            ...mapState('session', ['arzt']),
            ...mapState('patient', ['patientData', 'loaders']),
            ...mapState('authentication', ['impfstelle_id']),
            ...mapState('rezepte', ['rezepte']),
            ...mapState('rezepte', { rezepteLoaders: 'loaders' }),
            ...mapGetters('personen', ['getPersonen']),
            ...mapState('personen', { personenLoaders: 'loaders' }),
        },
        methods: {
            ...mapMutations('session', ['setSelectedArzt']),
            ...mapActions('patient', [
                'createRezepteintrag',
                'deleteRezepteintrag',
                'printRezepteintrag',
                'exportRezepteintrag',
            ]),
            ...mapActions('rezepte', ['loadRezepte']),
            ...mapActions('personen', ['loadPersonen']),
            ...mapMutations('patient', ['setDialogRezept']),
        },
    })
    export default class RezepteintragDetail extends Vue {
        @Prop() visible!: boolean;
        @Prop() value!: Rezepteintrag;

        public $refs!: {
            form: VForm;
            verabreichung: Vue;
        };

        public rules!: ValidationRules;
        public parseDate!: (input: string | null) => string | null;
        public formatDate!: (input: string | null) => string | null;
        public patientData!: Patient;
        public impfstelle_id!: number;
        public arzt!: number | null;
        public loadRezepte!: (payload?: LoadRezeptePayload) => void;
        public loadPersonen!: (payload?: LoadPersonenPayload) => void;
        public rezepte!: Rezept[];
        public createRezepteintrag!: (payload: Partial<Karteieintrag>) => void;
        public deleteRezepteintrag!: (payload: Rezepteintrag) => void;
        public printRezepteintrag!: (payload: Rezepteintrag) => void;
        public exportRezepteintrag!: (payload: Rezepteintrag) => void;
        public setDialogRezept!: (visible: boolean) => void;

        public data = new Rezepteintrag();
        public rezept = new Rezept();

        public verabreichungErrorMessage = '';

        public get isNew(): boolean {
            return (this.data.rezept?.patrezept_id ?? null) === null;
        }

        public get maxZeitraum(): string {
            return moment().format('YYYY-MM-DD');
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (!visible) return;

            if (this.value.patimpfung_id) {
                this.data = { ...this.value };
                this.data.rezept = this.data.rezept ?? new PatRezept();
            } else {
                this.data = new Rezepteintrag();
                this.data.patnr = this.patientData.patnr;
                this.data.impfstelle_id = this.impfstelle_id;

                this.resetValidation();
            }

            if (this.arzt) {
                this.data.person_id_durchf = this.arzt;
            }

            if (this.isNew) {
                this.loadRezepte();
                this.loadPersonen();
            }

            this.data.impfdatum = moment().format('YYYY-MM-DD');
            this.impfdatumErrorMessages = [];
            this.impfdatumFormatted = this.formatDate(this.data.impfdatum);
            this.verabreichungErrorMessage = '';

            if (this.data.rezept.verabreichung === null) {
                this.data.rezept.verabreichung = '';
            }

            if (this.$refs.verabreichung?.$refs.content) {
                (this.$refs.verabreichung.$refs.content as HTMLElement).innerHTML = '';
            }
        }

        @Watch('data.rezept.bezeichnung')
        public onChangeRezept(rezeptBezeichnung: Rezept | string): void {
            if (typeof rezeptBezeichnung === 'object') {
                const rezept = this.rezepte.find((rezept: Rezept) => {
                    return rezept.rezept_id === rezeptBezeichnung.rezept_id;
                });

                if (rezept) {
                    this.data.rezept.verabreichung = rezept.verabreichung;
                }
            }
        }

        public resetValidation(): void {
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        }

        public validateVerabreichung(): void {
            this.verabreichungErrorMessage = '';

            if (this.rezept.verabreichung === '<p></p>') {
                this.rezept.verabreichung = '';
            }

            for (const rule of [this.rules.required]) {
                const result = rule(this.data.rezept.verabreichung);
                if (result !== true) {
                    this.verabreichungErrorMessage = result as string;
                }
            }
        }

        public search = '';

        public onClickSave(): void {
            this.validateVerabreichung();

            if (!this.$refs.form.validate() ||
                this.impfdatumErrorMessages.length > 0 ||
                this.verabreichungErrorMessage !== '') {
                return;
            }

            if (typeof this.data.rezept.bezeichnung === 'string' && this.data.rezept.bezeichnung !== this.search) {
                this.data.rezept.bezeichnung = this.search;
            }

            this.createRezepteintrag({
                ...this.data,
                rezept: {
                    ...this.data.rezept,
                    bezeichnung: typeof this.data.rezept.bezeichnung === 'object'
                        ? (this.data.rezept.bezeichnung as unknown as Rezept).bezeichnung
                        : this.data.rezept.bezeichnung,
                },
            });
        }

        public onClickDelete(): void {
            confirmSwal('Löschen', 'Rezepteintrag endgültig löschen?', 'Löschen', 'Abbrechen')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.deleteRezepteintrag(this.data);
                    }
                });
        }

        public onClickPrint(): void {
            this.printRezepteintrag(this.data);
        }

        public onClickExport(): void {
            this.exportRezepteintrag(this.data);
        }

        public onClickClose(): void {
            this.resetValidation();
            this.setDialogRezept(false);
        }

        public impfdatumMenu = false;
        public impfdatumFormatted: string | null = null;
        public impfdatumErrorMessages: string[] = [];

        @Watch('data.impfdatum')
        public onChangeImpfdatum(date: string | null): void {
            this.impfdatumFormatted = this.formatDate(this.data.impfdatum);

            if (date !== null && this.isNew) {
                this.impfdatumErrorMessages = [];

                for (const rule of [this.rules.required, this.rules.validDate]) {
                    const valid = rule(this.data.impfdatum);
                    if (valid !== true) {
                        this.impfdatumErrorMessages.push(valid as string);
                    }
                }

                if (this.data.impfdatum && (this.data.impfdatum > moment().format('YYYY-MM-DD'))) {
                    this.impfdatumErrorMessages.push('Datum darf nicht in der Zukunft sein.');
                }
            }
        }

        public get verabreichungLabelClass(): string {
            return this.isNew
                ? this.verabreichungErrorMessage === ''
                    ? 'primary--text'
                    : 'red--text'
                : 'disabled-label';
        }
    }
